import { Button } from "@fieldwork/ui/components/button";
import { cn } from "@fieldwork/ui/lib/utils";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Fragment, type ReactNode, useEffect, useState } from "react";

import { NotConnected, useServer } from "@/components/server-hooks";
import { Note, ServerPage } from "@/components/server-shell";
import { Chip } from "@/components/ui-bits";
import { useAgentAction, useAgentQuery } from "@/lib/act";

export const Route = createFileRoute("/_auth/servers/$id/suggestions")({
  component: SuggestionsTab,
});

type Proposal = {
  id: string;
  keys: string[];
  runs: number[];
  at: string;
  op: string;
  project: string | null;
  plane_issue: string | null;
  title: string;
  priority: string | null;
  target_date: string | null;
  reason: string;
  details: string;
  status?: string;
  note?: string | null;
  /** epoch seconds, on decided ones */
  decided_at?: number | null;
};
type List = {
  pending: Proposal[];
  decided: Proposal[];
  applying: boolean;
  mode: string;
};

const OP_LABEL: Record<string, string> = {
  create: "New task",
  update: "Update",
  comment: "Comment",
  reprioritize: "Change priority",
  review: "Move to In Review",
  close: "Close",
};
const OPS = ["create", "update", "reprioritize", "comment", "review", "close"];
const taskKey = (k: string | null) => (k && /^[A-Z][A-Z0-9]*-\d+$/.test(k) ? k : null);
const due = (d: string) =>
  new Date(`${d}T00:00`).toLocaleDateString([], {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

/** "today 08:02", "yesterday", "Fri", or "3 Oct" for a decision time (epoch seconds). */
function decidedWhen(s: number | null | undefined) {
  if (!s) return "–";
  const d = new Date(s * 1000);
  const days = Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
  if (days <= 0) return `today ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  if (days === 1) return "yesterday";
  if (days < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
}

function SuggestionsTab() {
  const { id } = Route.useParams();
  const connected = useServer(id).data?.hub.connected;
  const q = useAgentQuery<List>(id, "proposals_list", {}, !!connected);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [kind, setKind] = useState<string | null>(null);
  const decide = useAgentAction<{ ids: string[]; decision: "approve" | "dismiss" }, { applying?: number; dismissed?: number }>(
    id,
    "proposals_decide",
    (v) => v,
    {
      refresh: ["proposals_list"],
      success: (r) => (r.applying ? `Applying ${r.applying} in Plane; this takes a minute or two` : `Dismissed ${r.dismissed}`),
    },
  );
  // while a batch is being applied, check back every few seconds
  useEffect(() => {
    if (!q.data?.applying) return;
    const t = setInterval(() => q.refetch(), 5000);
    return () => clearInterval(t);
  }, [q.data?.applying, q]);

  const d = q.data;
  const cycles = new Set(d?.pending.flatMap((p) => p.runs) ?? []).size;
  const subtitle = d
    ? `${d.pending.length} waiting${cycles ? ` · proposed in ${cycles === 1 ? "1 cycle" : `${cycles} cycles`}` : ""} · approved ones are applied in Plane by your server`
    : "Changes the agent proposed in suggest mode. Approve them to apply in Plane.";

  let body: ReactNode;
  if (!connected) body = <NotConnected />;
  else if (q.error) body = <p className="text-sm text-[#F48A80]">{q.error.message}</p>;
  else if (!d) body = <p className="text-sm text-[#A8A59D]">Loading…</p>;
  else {
    const act = (ids: string[], decision: "approve" | "dismiss") =>
      decide.mutate({ ids, decision }, { onSuccess: () => setPicked(new Set()) });
    const counts = new Map<string, number>();
    for (const p of d.pending) counts.set(p.op, (counts.get(p.op) ?? 0) + 1);
    const kinds = [...counts.keys()].sort((a, b) => (OPS.indexOf(a) + 1 || 99) - (OPS.indexOf(b) + 1 || 99));
    const filter = kind && counts.has(kind) ? kind : null;
    const shown = filter ? d.pending.filter((p) => p.op === filter) : d.pending;
    const selected = shown.filter((p) => picked.has(p.id)).map((p) => p.id);
    const allOn = shown.length > 0 && selected.length === shown.length;
    const applying = d.decided.filter((p) => p.status === "applying").length;

    body = (
      <>
        {d.mode !== "suggest" && <Note>This server applies changes itself now; these are proposals left from runs in suggest mode.</Note>}

        {d.applying && (
          <div
            role="status"
            className="flex items-center gap-3 rounded-md bg-[rgba(125,167,240,0.1)] px-4 py-3 text-[#A7C2F7] shadow-[inset_0_0_0_1px_rgba(125,167,240,0.3)]"
          >
            <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-[#A7C2F7]" />
            <span>
              Applying {applying ? `${applying} approved suggestion${applying > 1 ? "s" : ""}` : "approved suggestions"} in Plane… it checks
              first that each task doesn’t already exist.
            </span>
          </div>
        )}

        {d.pending.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md bg-[#1A1A19] px-4 py-3 shadow-[inset_0_0_0_1px_#2A2927]">
            <div className="flex flex-wrap items-center gap-3.5">
              <label className="flex min-h-10 items-center gap-2 text-[13px]">
                <input
                  type="checkbox"
                  className="size-4 accent-[#FF4F1A]"
                  checked={allOn}
                  disabled={decide.isPending}
                  onChange={() => {
                    const n = new Set(picked);
                    for (const p of shown) {
                      if (allOn) n.delete(p.id);
                      else n.add(p.id);
                    }
                    setPicked(n);
                  }}
                />
                Select all
              </label>
              <span className="text-[13px] text-[#A8A59D]">{selected.length} selected</span>
              <Button disabled={!selected.length || decide.isPending || d.applying} onClick={() => act(selected, "approve")}>
                Approve {selected.length || ""}
              </Button>
              <Button variant="outline" disabled={!selected.length || decide.isPending} onClick={() => act(selected, "dismiss")}>
                Dismiss {selected.length || ""}
              </Button>
            </div>
            {kinds.length > 1 && (
              <div role="group" aria-label="Filter by kind" className="flex flex-wrap gap-1.5">
                <FilterButton on={!filter} onClick={() => setKind(null)}>
                  All {d.pending.length}
                </FilterButton>
                {kinds.map((k) => (
                  <FilterButton key={k} on={filter === k} onClick={() => setKind(k)}>
                    {OP_LABEL[k] ?? k} {counts.get(k)}
                  </FilterButton>
                ))}
              </div>
            )}
          </div>
        )}

        <h2 className="m-0 mt-2 font-mono text-[15px] font-bold">Waiting for you</h2>
        {d.pending.length === 0 && <Note>Nothing waiting.</Note>}
        {shown.map((p) => {
          const key = taskKey(p.plane_issue);
          const checked = picked.has(p.id);
          const project = p.project ? (p.project.length > 20 ? "…" : p.project) : null;
          return (
            <article
              key={p.id}
              className={cn(
                "flex gap-4 rounded-md bg-[#1A1A19] px-[22px] py-5",
                checked ? "shadow-[inset_0_0_0_1px_#3A3935]" : "shadow-[inset_0_0_0_1px_#2A2927]",
              )}
            >
              <input
                type="checkbox"
                aria-label={`Select: ${p.title || p.reason}`}
                className="mt-1 size-4 shrink-0 accent-[#FF4F1A]"
                checked={checked}
                onChange={() => {
                  const n = new Set(picked);
                  if (checked) n.delete(p.id);
                  else n.add(p.id);
                  setPicked(n);
                }}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <Chip value="info" label={OP_LABEL[p.op] ?? p.op} />
                  {key ? (
                    <Link to="/servers/$id/tasks/$key" params={{ id, key }} className="font-mono text-[13px] text-[#A8A59D]">
                      {key}
                    </Link>
                  ) : (
                    p.plane_issue && <span className="font-mono text-[13px] text-[#A8A59D]">{p.plane_issue}</span>
                  )}
                  <h3 className="m-0 text-base font-semibold">
                    {p.title || p.reason}
                    {p.op === "reprioritize" && p.priority && (
                      <span className="font-mono text-[13px] font-normal text-[#A8A59D]"> → {p.priority}</span>
                    )}
                  </h3>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 font-mono text-xs text-[#A8A59D]">
                  {project && <span>project {project}</span>}
                  {p.priority && p.op !== "reprioritize" && <span>priority {p.priority}</span>}
                  {p.target_date && <span>due {due(p.target_date)}</span>}
                  <span>
                    proposed in run{p.runs.length > 1 ? "s" : ""}{" "}
                    {p.runs.map((r, i) => (
                      <Fragment key={r}>
                        {i > 0 && (i === p.runs.length - 1 ? " and " : ", ")}
                        <Link to="/servers/$id/runs/$rid" params={{ id, rid: String(r) }} className="text-[#A8A59D]">
                          {r.toLocaleString()}
                        </Link>
                      </Fragment>
                    ))}
                  </span>
                </div>
                {p.title && p.reason && <p className="m-0 text-[#CFCBC1]">{p.reason}</p>}
                {p.details && (
                  <details className="border-t border-[#2A2927] pt-2.5">
                    <summary className="cursor-pointer font-mono text-xs text-[#A8A59D]">Details</summary>
                    <p className="m-0 mt-2.5 rounded-[4px] bg-[#111111] px-3.5 py-3 whitespace-pre-wrap">{p.details}</p>
                  </details>
                )}
              </div>
              <div className="flex shrink-0 flex-col gap-2">
                <Button disabled={decide.isPending || d.applying} onClick={() => act([p.id], "approve")}>
                  Approve
                </Button>
                <Button variant="ghost" disabled={decide.isPending} onClick={() => act([p.id], "dismiss")}>
                  Dismiss
                </Button>
              </div>
            </article>
          );
        })}

        {d.decided.length > 0 && (
          <>
            <h2 className="m-0 mt-4 font-mono text-[15px] font-bold">Recently decided</h2>
            <div className="overflow-x-auto rounded-md bg-[#1A1A19] shadow-[inset_0_0_0_1px_#2A2927]">
              <table className="w-full min-w-[640px] border-collapse text-sm">
                <thead>
                  <tr className="text-left font-mono text-[11px] uppercase tracking-[0.08em] text-[#A8A59D]">
                    <th scope="col" className="px-[22px] py-3 font-medium">
                      Result
                    </th>
                    <th scope="col" className="px-[22px] py-3 font-medium">
                      Suggestion
                    </th>
                    <th scope="col" className="px-[22px] py-3 font-medium">
                      Task
                    </th>
                    <th scope="col" className="px-[22px] py-3 font-medium">
                      When
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {d.decided.map((p) => {
                    const key = taskKey(p.plane_issue);
                    return (
                      <tr key={p.id} className="border-t border-[#2A2927] align-top">
                        <td className="px-[22px] py-3">
                          <Chip
                            value={
                              p.status === "applied" || p.status === "existing"
                                ? "ok"
                                : p.status === "failed"
                                  ? "error"
                                  : p.status === "applying"
                                    ? "info"
                                    : "neutral"
                            }
                            label={p.status === "existing" ? "already in Plane" : (p.status ?? "")}
                          />
                        </td>
                        <td className="px-[22px] py-3">{p.title || p.reason}</td>
                        <td className="px-[22px] py-3">
                          {key ? (
                            <Link to="/servers/$id/tasks/$key" params={{ id, key }} className="font-mono">
                              {key}
                            </Link>
                          ) : (
                            p.plane_issue && <span className="font-mono">{p.plane_issue}</span>
                          )}
                          {p.note && <span className="text-[#A8A59D]">{p.plane_issue ? ` · ${p.note}` : p.note}</span>}
                          {!p.plane_issue && !p.note && <span className="text-[#A8A59D]">—</span>}
                        </td>
                        <td className="px-[22px] py-3 whitespace-nowrap text-[#A8A59D]">{decidedWhen(p.decided_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </>
    );
  }

  return (
    <ServerPage
      title="Suggestions"
      subtitle={subtitle}
      actions={
        d?.mode && (
          <Link
            to="/servers/$id/claude"
            params={{ id }}
            className={cn(
              "rounded-[4px] px-2.5 py-1.5 font-mono text-xs no-underline",
              d.mode === "suggest" ? "bg-[rgba(232,163,61,0.14)] text-[#F0B65A]" : "bg-[rgba(95,191,127,0.14)] text-[#7FD39B]",
            )}
          >
            {d.mode === "suggest" ? "Suggest mode" : "Apply mode"} · change
          </Link>
        )
      }
    >
      {body}
    </ServerPage>
  );
}

function FilterButton({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        "min-h-8 cursor-pointer rounded-[4px] border-0 px-2.5 font-mono text-xs",
        on ? "bg-[#ECEAE4] text-[#111111]" : "bg-[#262523] text-[#CFCBC1] hover:text-[#ECEAE4]",
      )}
    >
      {children}
    </button>
  );
}
